import { CONFIGURE_CELEBRATIONS_ELEMENT_ID } from '@admin/engagement/celebrations/routes'
import {
  CELEBRATION_POINTS_ELEMENT_ID,
  PEER_POINTS_ELEMENT_ID,
  POINTS_ELEMENT_ID,
} from '@admin/pointsAndRewards/points/routes'
import { SECURITY_ELEMENT_ID } from '@admin/userManagement/security/routes'
import AppPaths from '@globals/paths'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import { OnboardingContent } from '@nav/types'
import { T } from '@transifex/react'
import { Link as RouterLink } from 'react-router'
import HelpText from './HelpText'
import {
  CELEBRATION_POINTS_HELP_URL,
  PEER_TO_PEER_POINTS_HELP_URL,
  POINT_BANKS_HELP_URL,
  POINTS_PREFERENCES_HELP_URL,
  SECURITY_HELP_URL,
} from './constants'
import { ListStyled } from '@shared/dialog/HelpDrawer.styled'

type OnboardingGuideContentMap = Record<string, OnboardingContent>

// Create onboarding content that matches exactly the actionUrls
export const onboardingGuideContent: OnboardingGuideContentMap = {
  [AppPaths.tenant.admin.hrisIntegrations('records')]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="HRIS integration" />
        </Typography>
        <Typography variant="body1">
          <T _str="Automatically sync users from your HRIS into Kudos to keep your data accurate and up to date. Once set up, it's a low-maintenance option that saves time by reducing manual updates. Note that initial setup may require support from your IT team to ensure everything connects properly." />
        </Typography>
        <br />
        <Typography variant="body1">
          <T
            _str="You have full control over who gets invited to Kudos after the sync is set up. If you want to automate this process, turn on the {toggle} toggle when configuring your sync settings. Just make sure you're ready to launch before enabling it."
            toggle={
              <b>
                <span>
                  <T _str="Automatically invite new employees to Kudos" />
                </span>
              </b>
            }
          />
        </Typography>
        <br />
        <HelpText
          helpText={
            <T _str="Don’t see your HRIS provider? Ask our Support team if it can be added to the list of available integrations." />
          }
        />
      </>
    ),
    helpUrl:
      'https://kudosng.zendesk.com/hc/en-us/articles/38840666590733-How-to-import-employee-records-with-an-HRIS-integration',
  },
  [AppPaths.tenant.admin.configureFtp()]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="FTP integration" />
        </Typography>
        <Typography variant="body1">
          <T _str="File transfer protocol (FTP) is a secure way to move files between systems. Kudos supports automated user provisioning via the following file transfer protocols:" />
        </Typography>
        <br />
        <ListStyled>
          <li>
            <span>
              <T _str="SFTP (with username and password)" />
            </span>
          </li>
          <li>
            <span>
              <T _str="SFTP (with SSH key)" />
            </span>
          </li>
          <li>
            <span>
              <T _str="FTPS" />
            </span>
          </li>
        </ListStyled>
        <br />
        <Typography variant="body1">
          <T _str="This setup enables automated transfer of user data from a secure location to Kudos. It’s a reliable way to keep your user records in sync without manual uploads." />
        </Typography>
        <br />
        <Typography variant="body1">
          <T
            _str="Once your connection is active, user data will sync automatically once per day at midnight. You can also force a sync using the ‘Sync now’ option found on each connection on the {ftpIntegrations} page."
            ftpIntegrations={
              <b>
                <span>
                  <T _str="FTP integration > Connections" />
                </span>
              </b>
            }
          />
        </Typography>
        <br />
        <Typography variant="body1">
          <T
            _str="We recommend manually uploading your file through the bulk user upload tool ({bulkImportUsers}) first. This helps surface any formatting errors right away, giving you a chance to correct them before enabling automatic sync. Make sure any issues are fixed at the source so they don’t repeat with future uploads."
            bulkImportUsers={
              <b>
                <span>
                  <T _str="User management > Users & teams > Bulk import users" />
                </span>
              </b>
            }
          />
        </Typography>
      </>
    ),
    helpUrl:
      'https://kudosng.zendesk.com/hc/en-us/articles/38985388325261-How-to-import-HR-records-with-a-File-Transfer-Protocol-FTP-WIP',
  },

  [AppPaths.tenant.admin.members('quick-teams')]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="Quick teams" />
        </Typography>
        <Typography variant="body1">
          <T _str="Quick teams are automatically generated based on attributes like department and cost centre. These attributes come from your HRIS integration or the data included in manual user uploads." />
        </Typography>
        <br />
        <Typography variant="body1">
          <T
            _str="Click the preview link beside each attribute to see how many teams will be created and how many users will be included. When you're ready, click {save} to create the teams. You can update which quick teams you're using at any time by clicking {manage}."
            save={
              <b>
                <span>
                  <T _str="Save" />
                </span>
              </b>
            }
            manage={
              <b>
                <span>
                  <T _str="Manage quick teams" />
                </span>
              </b>
            }
          />
        </Typography>
        <br />
        <Typography variant="body1">
          <T _str="Quick teams stay in sync with your data, which means membership adjusts whenever user information, like department or location, changes in your HRIS or user uploads." />
        </Typography>
      </>
    ),
    helpUrl:
      'https://kudosng.zendesk.com/hc/en-us/articles/38840732130317-How-to-create-and-manage-Quick-teams',
  },
  [AppPaths.tenant.admin.members('teams')]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="Manual teams" />
        </Typography>
        <Typography variant="body1">
          <T _str="Manual teams are built by selecting specific users yourself. This gives you full control over who's included and is ideal for ad hoc groups, pilots, or one-off initiatives. Membership doesn't change unless you update it." />
        </Typography>
        <br />
        <Typography variant="body1">
          <T _str="If a user is archived, they’ll no longer appear in any manual teams they’re part of. If they’re reactivated later, they’ll automatically reappear in those same teams." />
        </Typography>
        <br />
        <Typography variant="body2">
          <T _str="Automated teams" />
        </Typography>
        <Typography variant="body1">
          <T _str="Automated teams are created using rules based on attributes like location and direct reports. As user data changes, team membership updates automatically, making it a hands-off option for keeping teams accurate over time." />
        </Typography>
      </>
    ),
    helpUrl:
      'https://kudosng.zendesk.com/hc/en-us/articles/38840746445709-How-to-create-and-manage-automated-teams',
  },
  [AppPaths.tenant.admin.security()]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="Single sign-on (SSO)" />
        </Typography>
        <Typography variant="body1">
          <T _str="SSO allows users to access Kudos using the same credentials they use for other tools in your organization, like email or internal systems. Instead of creating a separate password, users log in through your identity provider (IdP), such as Okta, Azure Active Directory, or Google Workspace. Once authenticated, they’re automatically signed in to Kudos." />
        </Typography>
        <br />
        <Typography variant="body2">
          <T _str="Multi-factor authentication (MFA)" />
        </Typography>
        <Typography variant="body1">
          <T _str="MFA adds an extra layer of protection by requiring users to verify their identity with both a password and a second factor, such as a code from their mobile device." />
        </Typography>
        <Typography variant="body1">
          <T _str="This helps prevent unauthorized access, even if a password is compromised." />
        </Typography>
        <br />
        <HelpText
          helpText={
            <T _str="If your organization uses SSO, your identity provider handles MFA, so you do not need to enable it in Kudos." />
          }
        />
        <br />
        <Typography variant="body2">
          <T _str="Password expiry" />
        </Typography>
        <Typography variant="body1">
          <T _str="This setting controls how often users are required to update their password. Once the time limit is reached, they’ll be prompted to create a new one at their next login." />
        </Typography>
        <br />
        <Typography variant="body1">
          <T _str="Security best practices vary by industry, but common recommendations include:" />
        </Typography>
        <ListStyled>
          <li>
            <span>
              <T
                _str="{interval}: Suitable for low-risk environments with MFA"
                interval={
                  <b>
                    <span>
                      <T _str="Every 180 days" />
                    </span>
                  </b>
                }
              />
            </span>
          </li>
          <li>
            <span>
              <T
                _str="{interval}: A good balance of security and usability"
                interval={
                  <b>
                    <span>
                      <T _str="Every 90 days" />
                    </span>
                  </b>
                }
              />
            </span>
          </li>
          <li>
            <span>
              <T
                _str="{interval}: Ideal for high-security teams or when MFA isn’t in use"
                interval={
                  <b>
                    <span>
                      <T _str="Every 30–60 days" />
                    </span>
                  </b>
                }
              />
            </span>
          </li>
        </ListStyled>
      </>
    ),
    helpUrl: SECURITY_HELP_URL,
    routeElementId: SECURITY_ELEMENT_ID,
  },
  [AppPaths.tenant.admin.customize()]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="Branding" />
        </Typography>
        <Typography variant="body1">
          <T _str="Add your logo and banner to make Kudos feel like part of your organization, not just another tool. Strong branding builds trust, boosts engagement, and reinforces your company's identity." />
        </Typography>
        <br />
        <Typography variant="body2">
          <T _str="Logo:" />
        </Typography>
        <Typography variant="body1">
          <T _str="Use a high-quality, transparent image so it appears clean across the platform. Your logo shows in the side navigation, login page, and emails." />
        </Typography>
        <br />
        <Typography variant="body2">
          <T _str="Banner:" />
        </Typography>
        <Typography variant="body1">
          <T _str="Choose a simple, on-brand image. Illustrations, subtle textures, or uplifting photos work best. Avoid text-heavy graphics. Your banner appears at the top of the home page." />
        </Typography>
      </>
    ),
  },
  [AppPaths.tenant.admin.values()]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="Values and qualities" />
        </Typography>
        <Typography variant="body1">
          <T _str="Use your official company values as the foundation to keep recognition aligned with what your organization already prioritizes. Then add qualities that reflect real behaviours you want to encourage. These can be more informal, everyday traits that bring your values to life." />
        </Typography>
        <br />
        <Typography variant="body1">
          <T
            _str="Users will be able to tag recognition messages with qualities, and you'll be able to track usage trends in the {analytics} tab to see what's resonating across your organization."
            analytics={
              <b>
                <span>
                  <T _str="Analytics" />
                </span>
              </b>
            }
          />
        </Typography>
        <br />
        <HelpText
          helpText={
            <T _str="Stick to 1-2 words for both values and qualities to keep them clear and easy to select." />
          }
        />
      </>
    ),
  },
  [AppPaths.tenant.admin.pointsRules('peer')]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="Peer-to-peer points" />
        </Typography>
        <Typography variant="body1">
          <T
            _str="Each user has a bucket of points they can use to gift colleagues when sending recognition. These are called peer-to-peer points. {rules} control who receives how many. You can apply them across your entire organization or target specific teams you've set up."
            rules={
              <b>
                <span>
                  <T _str="Rules" />
                </span>
              </b>
            }
          />
        </Typography>
        <br />
        <Typography variant="body1">
          <T _str="The ideal minimum monthly allocation is 75 to 100 points per user, but you can set this to anything that fits your budget." />
        </Typography>
        <br />
        <Typography variant="body1">
          <T
            _str="Peer-to-peer point allocations reset at the end of each cycle—either monthly or quarterly, depending on your {preferences}. At that time, any unused points expire, and each user's balance is replenished with a full allocation."
            preferences={
              <Link component={RouterLink} to={AppPaths.tenant.admin.points('preferences')}>
                <span>
                  <T _str="Preferences" />
                </span>
              </Link>
            }
          />
        </Typography>
        <br />
        <Typography variant="body1">
          <T _str="If an individual runs out of points in an allocation period, they can continue to send recognition without points." />
        </Typography>
      </>
    ),
    helpUrl: PEER_TO_PEER_POINTS_HELP_URL,
    routeElementId: PEER_POINTS_ELEMENT_ID,
  },
  [AppPaths.tenant.admin.pointsRules('celebration')]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="Celebration points" />
        </Typography>
        <Typography variant="body1">
          <T _str="Celebrations let you automatically reward users with points for milestones like birthdays, anniversaries, or onboarding. They're a simple way to boost morale and make employees feel valued." />
        </Typography>
        <br />
        <Typography variant="body1">
          <T
            _str="Celebration point recommendations are based off of your point to dollar ratio, which you configure when setting up your {rewardsStore}:"
            rewardsStore={
              <b>
                <span>
                  <T _str="rewards store" />
                </span>
              </b>
            }
          />
        </Typography>
        <ListStyled>
          <li>
            <span>
              <T _str="Birthday: $10 in points" />
            </span>
          </li>
          <li>
            <span>
              <T _str="Welcome & onboarding milestones: $5 in points" />
            </span>
          </li>
          <li>
            <span>
              <T _str="Work anniversary:" />
            </span>
            <ul style={{ paddingLeft: '20px' }}>
              <li>
                <span>
                  <T _str="Year 1: $10 in points" />
                </span>
              </li>
              <li>
                <span>
                  <T _str="Year 2: $15 in points" />
                </span>
              </li>
              <li>
                <span>
                  <T _str="Year 3: $20 in points" />
                </span>
              </li>
              <li>
                <span>
                  <T _str="Year 4: $25 in points" />
                </span>
              </li>
              <li>
                <span>
                  <T _str="Year 5: $50 in points" />
                </span>
              </li>
              <li>
                <span>
                  <T _str="Year 10+: $100 in points" />
                </span>
              </li>
              <li>
                <span>
                  <T _str="Year 15+: $250 in points" />
                </span>
              </li>
              <li>
                <span>
                  <T _str="Year 20+: $500 in points" />
                </span>
              </li>
            </ul>
          </li>
        </ListStyled>
        <br />
        <HelpText
          helpText={
            <T _str="Users must have a start date and birthday set in their profile to receive celebration points." />
          }
        />
      </>
    ),
    helpUrl: CELEBRATION_POINTS_HELP_URL,
    routeElementId: CELEBRATION_POINTS_ELEMENT_ID,
  },
  [AppPaths.tenant.admin.pointsRules('bank')]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="Point banks" />
        </Typography>
        <Typography variant="body1">
          <T _str="Point banks are used to fund awards and incentives. You can set up any number of banks to match your program's structure. For example:" />
        </Typography>
        <ListStyled>
          <li>
            <span>
              <T _str="by cost centre" />
            </span>
          </li>
          <li>
            <span>
              <T _str="by department" />
            </span>
          </li>
          <li>
            <span>
              <T _str="by initiative (e.g. employee of the month)" />
            </span>
          </li>
        </ListStyled>

        <Typography variant="body1">
          <T _str="Specific awards or incentives can be linked to these banks, making it easier to manage spend, track usage, and keep each item accountable to its own budget." />
        </Typography>
      </>
    ),
    helpUrl: POINT_BANKS_HELP_URL,
  },
  [AppPaths.tenant.admin.points('preferences')]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="Disabling points" />
        </Typography>
        <Typography variant="body1">
          <T _str="If your organization doesn't want to include points as part of your recognition program, you can disable them here. This keeps all engagement features active (like sending and receiving recognition) while removing any reference to points across the platform." />
        </Typography>
        <br />
        <Typography variant="body2">
          <T _str="Peer-to-peer points allocation period" />
        </Typography>
        <Typography variant="body1">
          <T _str="This determines how often users receive their peer-to-peer points budgets." />
        </Typography>
        <br />
        <T
          _str="{monthly} works best if you want to:"
          monthly={
            <b>
              <span>
                <T _str="Monthly" />
              </span>
            </b>
          }
        />
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          <li>
            <span>
              <T _str="encourage regular recognition" />
            </span>
          </li>
          <li>
            <span>
              <T _str="track trends more frequently" />
            </span>
          </li>
        </ul>
        <T
          _str="{quarterly} works best if you want to:"
          quarterly={
            <b>
              <span>
                <T _str="Quarterly" />
              </span>
            </b>
          }
        />
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          <li>
            <span>
              <T _str="focus recognition on bigger, less frequent milestones" />
            </span>
          </li>
          <li>
            <span>
              <T _str="build more anticipation around rewards" />
            </span>
          </li>
        </ul>

        <br />
        <Typography variant="body1">
          <T _str="Any unused points expire at the end of each cycle. The balance then resets to the full amount for the next period to keep recognition flowing. For example: if someone receives 100 points per month and only uses 20, the remaining 80 expire and their balance resets to 100 in the next cycle." />
        </Typography>
        <br />
        <Typography variant="body2">
          <T _str="Redeemable points expire..." />
        </Typography>
        <Typography variant="body1">
          <T _str="Redeemable points are the points users earn from recognition, awards, or incentives, and can be used in your rewards store. If an expiry period is set, users must redeem their points within that window. Any unused balance will be cleared at the end of each cycle." />
        </Typography>
        <br />
        <Typography variant="body1">
          <T _str="If you enable point expiry, make sure to clearly communicate this to users to avoid confusion or frustration when points disappear." />
        </Typography>
        <br />
        <HelpText
          helpText={
            <T
              _str="Track unredeemed points in the {liabilities} tab."
              liabilities={
                <b>
                  <span>
                    <T _str="Liabilities" />
                  </span>
                </b>
              }
            />
          }
        />
        <br />
        <Typography variant="body2">
          <T _str="Exclude users from receiving redeemable points" />
        </Typography>
        <Typography variant="body1">
          <T _str="This setting determines which users should be excluded from receiving redeemable points from any source in your program. This setup is common when point budgets are limited to specific roles or employee groupings. When points are enabled for your program, all users are eligible to receive redeemable points by default. Users in the selected teams will become ineligible." />
        </Typography>
      </>
    ),
    helpUrl: POINTS_PREFERENCES_HELP_URL,
    routeElementId: POINTS_ELEMENT_ID,
  },
  [AppPaths.tenant.admin.configureCelebrations('privacy')]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="Celebration privacy" />
        </Typography>
        <Typography variant="body1">
          <T
            _str="You control which celebration types are visible across the platform. Keeping an event public helps foster connection and positivity by giving your team more chances to celebrate one another. They're shown on the {celebrate} tab, appear in the {upcoming} section of the homepage, and can include an accompanying card that employees will be invited to sign."
            celebrate={
              <b>
                <span>
                  <T _str="Celebrate" />
                </span>
              </b>
            }
            upcoming={
              <b>
                <span>
                  <T _str="Upcoming and important" />
                </span>
              </b>
            }
          />
        </Typography>
        <br />
        <Typography variant="body1">
          <T _str="Setting an event to private means it won't appear publicly in the platform, essentially choosing not to celebrate that event in Kudos. This can be helpful if you want to keep celebrations focused on key milestones, or avoid highlighting events that may not be relevant to your company culture." />
        </Typography>
        <br />
        <HelpText
          helpText={
            <T
              _str="If you've assigned points to a celebration type in your {pointsAllocation} but choose to keep it private, users will still receive their points privately via email."
              pointsAllocation={
                <b>
                  <span>
                    <T _str="Points allocation" />
                  </span>
                </b>
              }
            />
          }
        />
      </>
    ),
    helpUrl:
      'https://kudosng.zendesk.com/hc/en-us/articles/38839976063117-How-to-configure-Celebrations-and-Cards',
    routeElementId: CONFIGURE_CELEBRATIONS_ELEMENT_ID,
  },
  [AppPaths.tenant.admin.configureCelebrations('cards')]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="Cards" />
        </Typography>
        <Typography variant="body1">
          <T _str="Cards are a meaningful part of the celebration experience, helping users feel seen and appreciated during milestones and moments that matter. You can upload your own branded or on-theme card covers to reflect your company culture and make the experience feel more personal. You can also set default messages for automatically created cards, so the tone and language match your company's voice." />
        </Typography>
        <br />
        <Typography variant="body1">
          <T _str="If you want to go beyond standard events, you can also create custom cards for any occasion." />
        </Typography>
      </>
    ),
  },
  [AppPaths.tenant.admin.rewards('setup')]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="Reward stores" />
        </Typography>
        <Typography variant="body1">
          <T _str="A reward store is where users redeem their earned points for real-world rewards such as gift cards, merchandise, charitable donations, or your own selection of custom rewards like PTO, branded items or raffle tickets." />
        </Typography>
        <br />
        <Typography variant="body1">
          <T _str="Available rewards depend on the type of store and catalogue you choose." />
        </Typography>
        <br />
        <Typography variant="body1">
          <T _str="You can set up multiple reward stores to:" />
        </Typography>
        <ListStyled>
          <li>
            <span>
              <T _str="make them region specific" />
            </span>
          </li>
          <li>
            <span>
              <T _str="assign them to different budgets or cost centres" />
            </span>
          </li>
          <li>
            <span>
              <T _str="launch limited-time stores for holidays or campaigns" />
            </span>
          </li>
        </ListStyled>
        <br />
        <Typography variant="body1">
          <T
            _str="Users only see the stores they're eligible for in the {rewards} tab."
            rewards={
              <b>
                <span>
                  <T _str="Rewards" />
                </span>
              </b>
            }
          />
        </Typography>
      </>
    ),
    helpUrl:
      'https://kudosng.zendesk.com/hc/en-us/articles/38839623670797-How-to-create-and-manage-reward-stores',
  },
  [AppPaths.tenant.admin.awards('awards')]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="Awards" />
        </Typography>
        <Typography variant="body1">
          <T _str="A strong awards program includes a mix of award types to keep recognition fresh and meaningful for everyone. For awards that benefit from input across the organization, you can enable nominations. This adds visibility and fairness, but still keeps final review and approval in your control." />
        </Typography>
        <br />
        <Typography variant="body1">
          <T _str="Some organizations keep awards admin-managed to ensure consistency and fairness, especially for larger or more formal programs. Others expand award-giving to people leaders and managers, allowing them to celebrate team wins in real time." />
        </Typography>
        <br />
        <HelpText
          helpText={
            <T
              _str="Need help getting started? Take a look at our awards {templateLibrary}."
              templateLibrary={
                <Link
                  rel="noopener noreferrer"
                  href="https://kudosng.zendesk.com/hc/en-us/sections/38790390973581-Template-Library"
                  target="_blank">
                  <span>
                    <T _str="template library" />
                  </span>
                </Link>
              }
            />
          }
        />
      </>
    ),
    helpUrl: 'https://kudosng.zendesk.com/hc/en-us/articles/38840969033869-How-to-create-an-award',
  },
  [AppPaths.tenant.admin.manageIncentives()]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="Incentives" />
        </Typography>
        <Typography variant="body1">
          <T _str="Incentives are a great way to drive participation in key initiatives, reward effort, and keep users engaged beyond day-to-day recognition. You can use them to support behaviours that align with your culture, business goals, or engagement programs." />
        </Typography>
        <br />
        <Typography variant="body1">
          <T _str="Common examples include:" />
        </Typography>
        <ListStyled>
          <li>
            <span>
              <T _str="Completing training or certifications" />
            </span>
          </li>
          <li>
            <span>
              <T _str="Participating in wellness challenges" />
            </span>
          </li>
          <li>
            <span>
              <T _str="Submitting surveys or feedback" />
            </span>
          </li>
          <li>
            <span>
              <T _str="Participating in a peer recognition challenge (e.g. send 5 recognitions this month)" />
            </span>
          </li>
        </ListStyled>
        <br />
        <Typography variant="body1">
          <T _str="Incentives can be one-time or ongoing, and are fully customizable. You can shape them around what matters most to your organization." />
        </Typography>
        <br />
        <HelpText
          helpText={
            <T
              _str="Need help getting started? Take a look at our incentives {templateLibrary}."
              templateLibrary={
                <Link
                  href="https://kudosng.zendesk.com/hc/en-us/sections/38790390973581-Template-Library"
                  target="_blank"
                  rel="noopener noreferrer">
                  <span>
                    <T _str="template library" />
                  </span>
                </Link>
              }
            />
          }
        />
      </>
    ),
    helpUrl:
      'https://kudosng.zendesk.com/hc/en-us/articles/38839564255245-How-to-create-and-manage-incentives',
  },
  [AppPaths.tenant.admin.members('users')]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="Add users manually" />
        </Typography>
        <Typography variant="body1">
          <T _str="Manually add a single user at a time. This method is best for small teams, pilot groups, or adding individual users outside of bulk processes." />
        </Typography>
        <br />
        <Typography variant="body2">
          <T _str="Bulk import users with CSV" />
        </Typography>
        <Typography variant="body1">
          <T _str="Use the downloadable template to ensure correct formatting. This method is quick for large, one-time uploads, but it doesn't sync automatically. You'll need to re-import the file whenever updates are required." />
        </Typography>
        <br />
        <HelpText
          helpText={
            <T
              _str="If you haven't finished setup, make sure the {option} option is unchecked. This lets you complete setup and invite users when you're ready to launch."
              option={
                <b>
                  <span>
                    <T _str="Send invite email" />
                  </span>
                </b>
              }
            />
          }
        />
        <Typography mt={2} variant="body2">
          <T _str="Send invite email" />
        </Typography>
        <Typography variant="body1">
          <T
            _str="You can bulk select which users to invite from the {users} table. If you’re rolling out Kudos gradually (by department, for example) you can use the filters to narrow down your list. This gives you full control over who gets access and when."
            users={
              <b>
                <span>
                  <T _str="Users" />
                </span>
              </b>
            }
          />
        </Typography>
        <br />
        <Typography variant="body1">
          <T
            _str="You’ll be able to see who has been sent an invite and who has joined by checking the {userStatus} column in the table."
            userStatus={
              <b>
                <span>
                  <T _str="User status" />
                </span>
              </b>
            }
          />
        </Typography>
        <br />
        <HelpText
          helpText={
            <T
              _str="Check back on the users table to monitor your {activationRate}. A strong activation rate means users are joining the platform and starting to engage. It’s a key indicator of a successful launch."
              activationRate={
                <b>
                  <span>
                    <T _str="activation rate" />
                  </span>
                </b>
              }
            />
          }
        />
      </>
    ),
    helpUrl:
      'https://kudosng.zendesk.com/hc/en-us/articles/38840660008333-How-to-import-employee-records-with-a-CSV-file',
  },
  [AppPaths.tenant.admin.announcements()]: {
    content: (
      <>
        <Typography variant="body2">
          <T _str="Announcements" />
        </Typography>
        <Typography variant="body1">
          <T
            _str="{announcements} are a powerful way to keep your team informed, aligned, and engaged. Use them to share important updates, celebrate wins, highlight new initiatives, or reinforce key messages across the company."
            announcements={
              <b>
                <span>
                  <T _str="Announcements" />
                </span>
              </b>
            }
          />
        </Typography>
        <br />
        <Typography variant="body1">
          <T
            _str="They appear right on the homepage timeline and can be pinned to the {upcoming} section for extra visibility. You can even chose to send an email version of your announcement. "
            upcoming={
              <b>
                <span>
                  <T _str="Upcoming and important" />
                </span>
              </b>
            }
          />
        </Typography>
        <br />
        <Typography variant="body1">
          <T _str="Go ahead and try it out by creating your launch announcement. Be sure to include what Kudos is, how it will benefit your team, and any next steps." />
        </Typography>
        <br />
        <HelpText
          helpText={
            <T
              _str="Need help getting started? Take a look at our announcements {templateLibrary}."
              templateLibrary={
                <Link
                  href="https://kudosng.zendesk.com/hc/en-us/sections/38790390973581-Template-Library"
                  target="_blank"
                  rel="noopener noreferrer">
                  <span>
                    <T _str="template library" />
                  </span>
                </Link>
              }
            />
          }
        />
      </>
    ),
    helpUrl:
      'https://kudosng.zendesk.com/hc/en-us/articles/38839540755085-How-to-create-an-announcement',
  },
}
